import { min as buildSegmentStub } from "@segment/snippet";

/**
 * Hash parameters worth attributing a page view to. An allow-list, so arbitrary fragments
 * never reach the analytics payload.
 */
const TRACKED_HASH_PARAMS = ["a", "source", "dest", "campaign", "ref"];

/**
 * Our own `page` call, replacing the generator's. Reports the pathname with
 * `internalRef`/`internalReferrer`, plus any allow-listed hash parameters. The hash block
 * attributes the promo entry points, which arrive at `/#a=promo&source=…`.
 */
const SEGMENT_PAGE_CALL = `
      var trackedHash = "";
      if (window.location.hash) {
        try {
          var hashParams = new URLSearchParams(window.location.hash.substring(1));
          var trackedParams = new URLSearchParams();
          ${JSON.stringify(TRACKED_HASH_PARAMS)}.forEach(function (name) {
            if (hashParams.has(name)) {
              trackedParams.set(name, hashParams.get(name));
            }
          });
          if (trackedParams.toString()) {
            trackedHash = "#" + trackedParams.toString();
          }
        } catch (error) {
          // Datadog RUM is not up this early, and an unparseable hash must not cost the
          // page call itself.
        }
      }
      analytics.page(window.location.pathname, Object.assign({ internalRef: window.location.search, internalReferrer: document.referrer }, trackedHash ? { hash: trackedHash } : {}));
`;

/**
 * The inline Segment snippet: the generator's stub with our `page` call spliced in. Must
 * run before hydration or events fired as components mount find no queue — hence the
 * `beforeInteractive` tag in `AnalyticsScripts`. The page call goes inside the stub's
 * `else` branch, so a snippet included twice still reports one page view.
 */
export const buildSegmentSnippet = ({
  segmentCustomDomain,
  segmentKey,
}: {
  segmentCustomDomain: string;
  segmentKey: string;
}): string => {
  const stub = buildSegmentStub({
    apiKey: segmentKey,
    host: segmentCustomDomain,
    useHostForBundles: true,
    // The consent wrapper owns `load`; ours is the `page` call spliced in below.
    load: false,
    page: false,
  });

  // `}}();` closes the stub's `else` branch and its IIFE. A shape change upstream would
  // drop the page call silently, so a test asserts the result still carries it.
  return stub.replace(/\s*\}\}\(\);$/, `${SEGMENT_PAGE_CALL}}}();`);
};
